import { match } from "ts-pattern";

import { FormSectionSchema } from "../../types/FormSection.js";
import {
  IncidentFormTypeSchema,
  type IncidentForm,
} from "../../types/incidentWorkItem.js";
import {
  type RecordPolicePresenceMinimumFields,
  type RequestPolicePresenceMinimumFields,
} from "../../types/workflow/index.js";
import type { ActionInput } from "../ActionInput.js";
import { calculateIncidentWorkItemStatus } from "../calculateIncidentWorkItemStatus.js";
import { getSupportedIncidentTypeSelection } from "../helpers.js";

export type MarkPolicePresenceAction = {
  formsRequiringPoliceResponse: { formId: string }[];
  completedPolicePresenceForms: { formId: string }[];
  policeCanRespond: boolean;
  canEnterOfficerRequestInformation: boolean;
};

export function calculatePolicePresenceActions(
  actionInput: ActionInput,
): MarkPolicePresenceAction {
  // get a sorted list of all request police presence forms
  const requestPolicePresenceForms = actionInput.incidentWorkItem.forms
    .filter(
      (form) =>
        form.formType === IncidentFormTypeSchema.enum.requestPolicePresence,
    )
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const isFormFinished = (form: IncidentForm) => {
    const requestPolicePresenceFormSection = form.sections.find(
      (section) =>
        section.id === FormSectionSchema.enum.requestPolicePresenceMain,
    );

    const recordPolicePresenceFormSection = form.sections.find(
      (section) => section.id === FormSectionSchema.enum.recordPolicePresence,
    );

    // When the NHS side has requested police presence, the police must respond
    return !(
      requestPolicePresenceFormSection &&
      ((
        requestPolicePresenceFormSection.data as RequestPolicePresenceMinimumFields
      ).source?.value ?? "emha") === "emha" &&
      (
        requestPolicePresenceFormSection.data as RequestPolicePresenceMinimumFields
      ).hasPolicePresenceRequested.value === "yes" &&
      (!recordPolicePresenceFormSection ||
        !recordPolicePresenceFormSection.data ||
        !(
          recordPolicePresenceFormSection.data as Partial<RecordPolicePresenceMinimumFields>
        ).hasRemainedAtPlaceOfSafety)
    );
  };

  const isInCorrectStatusToRespondToNhsRequests = match(
    calculateIncidentWorkItemStatus(actionInput.incidentWorkItem).unwrapOr(
      "unknown",
    ),
  )
    .with(
      "police-presence-not-requested",
      "police-presence-requested",
      () => true,
    )
    .with(
      "in-progress",
      "awaiting-arrival",
      "arrived-at-place-of-safety",
      "ended",
      "form-shared",
      "nhs-form-accepted",
      "unknown",
      () => false,
    )
    .exhaustive();

  const isCorrectIncidentTypeToSubmitOfficerRequests = match(
    getSupportedIncidentTypeSelection({
      incidentWorkItem: actionInput.incidentWorkItem,
    })
      // .orTee((e) => {
      //   console.error("Unsupported incident type", e.prettifyError());
      // })
      .unwrapOr(undefined),
  )
    .with("136", () => true)
    .with("mca", () => false)
    .with(undefined, () => false)
    .exhaustive();

  const isInCorrectStatusToSubmitOfficerRequests = match(
    calculateIncidentWorkItemStatus(actionInput.incidentWorkItem).unwrapOr(
      "unknown",
    ),
  )
    .with(
      "police-presence-not-requested",
      "police-presence-requested",
      "form-shared",
      "nhs-form-accepted",
      () => true,
    )
    .with(
      "arrived-at-place-of-safety",
      "in-progress",
      "awaiting-arrival",
      "ended",
      "unknown",
      () => false,
    )
    .exhaustive();

  const userIsOwner = actionInput.incidentWorkItem.participants.find(
    (participant) =>
      participant.participantId === actionInput.user.id &&
      participant.role === "owner",
  )
    ? true
    : false;

  const canEnterOfficerRequestInformation =
    userIsOwner &&
    isCorrectIncidentTypeToSubmitOfficerRequests &&
    isInCorrectStatusToSubmitOfficerRequests;

  return {
    formsRequiringPoliceResponse: requestPolicePresenceForms
      .filter((form) => !isFormFinished(form))
      .map((form) => ({ formId: form.id })),
    completedPolicePresenceForms: requestPolicePresenceForms
      .filter((form) => isFormFinished(form))
      .map((form) => ({ formId: form.id })),
    policeCanRespond: isInCorrectStatusToRespondToNhsRequests && userIsOwner,
    canEnterOfficerRequestInformation,
  };
}
